import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, ClipboardList, HandHeart, Lightbulb, Sparkles, ChevronRight, Sprout, LifeBuoy } from 'lucide-react';
import { rise, SKILL_STYLE, SKILL_LABEL } from './shared';

function TodoCard({ icon: Icon, text, sub, btn, onClick, i }) {
  return (
    <div className="td-rise td-card bg-card border border-border rounded-lg p-4 flex items-center gap-3 flex-wrap" style={rise(i)}>
      <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm text-foreground">{text}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <button onClick={onClick} data-testid={`td-todo-btn-${i}`}
        className="flex items-center gap-1 text-xs font-medium text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 rounded-full px-3.5 py-1.5 transition-colors">
        {btn} <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function HomeTab({ gotoTab }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.teamDevHome().then((res) => setData(res.data)).catch(() => toast.error('Konnte nicht geladen werden'));
  }, []);

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  const todoCards = [];
  (data.todos || []).forEach((t) => {
    if (t.kind === 'checkin_open') todoCards.push({ icon: ClipboardList, text: 'Dein Wochen-Check-in ist noch offen', sub: '3 Minuten, dann ist die Woche rund. ✨', btn: 'Jetzt ausfüllen', onClick: () => gotoTab('checkin') });
    if (t.kind === 'requests_open') todoCards.push({ icon: HandHeart, text: t.count === 1 ? `${t.next?.from_name} wünscht sich dein Feedback` : `${t.count} Kolleg:innen wünschen sich dein Feedback`, sub: t.next ? `„${t.next.topic}“` : '', btn: 'Ansehen', onClick: () => gotoTab('requests') });
    if (t.kind === 'goal_stale') todoCards.push({ icon: Sprout, text: `Dein Ziel „${t.title}“ hatte länger kein Update`, sub: 'Das heißt nichts Schlechtes — ein kurzer Status im Check-in genügt.', btn: 'Status setzen', onClick: () => gotoTab('checkin') });
    if (t.kind === 'ideas_unreacted') todoCards.push({ icon: Lightbulb, text: t.count === 1 ? 'Eine Idee wartet auf deine Reaktion' : `${t.count} Ideen warten auf deine Reaktion`, sub: 'Der Ideengeber sieht nur, DASS du reagiert hast — nicht wie.', btn: 'Zum Postfach', onClick: () => gotoTab('inbox') });
  });

  return (
    <div className="space-y-7" data-testid="td-home">
      <div className="space-y-2.5">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Deine To-dos</p>
        {todoCards.length === 0 ? (
          <div className="td-rise bg-card border border-border rounded-lg p-4 flex items-center gap-3" data-testid="td-todos-empty">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <p className="text-sm text-foreground">Alles rund — keine offenen To-dos. Schön! ✨</p>
          </div>
        ) : todoCards.map((c, i) => <TodoCard key={i} {...c} i={i} />)}
      </div>

      {(data.goals_bar || []).length > 0 && (
        <div className="space-y-2.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Woran das Team gerade wächst</p>
          <div className="flex flex-wrap gap-2">
            {data.goals_bar.map((g, i) => (
              <button key={g.id} onClick={() => gotoTab('goals')} style={rise(i)} data-testid={`td-goalbar-${i}`}
                className="td-rise td-card flex items-center gap-2 bg-card border border-border rounded-full pl-2.5 pr-3.5 py-1.5 text-xs hover:border-emerald-500/40">
                <span className="text-sm">{g.stage}</span>
                <span className="font-medium text-foreground">{g.owner_name?.split(' ')[0]}</span>
                <span className="text-muted-foreground truncate max-w-[220px]">{g.title}</span>
                <span className={`text-[8px] font-mono uppercase border rounded-full px-1.5 py-px ${SKILL_STYLE[g.skill_type]}`}>{SKILL_LABEL[g.skill_type]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2.5">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Team-Feed — Highlights & Kudos</p>
        {(data.feed || []).length === 0 ? (
          <div className="td-rise bg-card border border-dashed border-border rounded-lg p-8 text-center" data-testid="td-feed-empty">
            <p className="text-2xl mb-2">🌱</p>
            <p className="text-sm text-foreground">Noch still hier.</p>
            <p className="text-xs text-muted-foreground mt-1">Der Feed füllt sich mit den Highlights aus den Wochen-Check-ins.</p>
            <button onClick={() => gotoTab('checkin')} data-testid="td-feed-empty-cta"
              className="mt-3 text-xs font-medium text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 rounded-full px-4 py-1.5 transition-colors">
              Mach den Anfang — dein Check-in
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {data.feed.map((f, i) => (
              <div key={i} style={rise(i)} data-testid={`td-feed-item-${i}`}
                className="td-rise bg-card border border-border rounded-lg px-4 py-3 flex gap-3 items-start">
                <span className="text-base mt-0.5">{f.kind === 'kudos' ? '💛' : '🌟'}</span>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{f.user_name}</span>
                    {f.kind === 'kudos' && <> → <span className="font-medium text-foreground">{f.to_name}</span></>}
                    {f.week_key && <span className="font-mono text-[9px] ml-2 border border-border rounded px-1 py-px">{f.week_key}</span>}
                  </p>
                  <p className="text-sm text-foreground/90 mt-0.5 leading-relaxed">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(data.blockers || []).length > 0 && (
        <div className="space-y-2.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <LifeBuoy className="w-3 h-3" /> Blocker diese Woche — wo können wir helfen?
          </p>
          {data.blockers.map((b, i) => (
            <div key={i} style={rise(i)} className="td-rise bg-card border border-sky-500/20 rounded-lg px-4 py-3" data-testid={`td-blocker-${i}`}>
              <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{b.user_name}</span></p>
              <p className="text-sm text-foreground/90 mt-0.5">{b.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
